import React, { useEffect, useMemo, useState } from 'react';
import { Info, Layers } from 'lucide-react';
import { ResolvedPeriod } from '../PeriodPicker';
import { BreakdownRow, QualityApi } from './qualityApi';
import { DrillDownModal } from './DrillDownModal';

type Metric = 'count' | 'open' | 'density';

const METRIC_LABEL: Record<Metric, string> = {
  count: 'Count',
  open: 'Count (open only)',
  density: 'Density (bugs / stories)',
};

const METRIC_HINT: Record<Metric, string> = {
  count: 'Total bugs in this component during the selected Period.',
  open: 'Bugs currently in an open status (not Done / Resolved).',
  density:
    'Bugs ÷ stories delivered in this component over the Period. Use this to compare fairly — large components naturally have more raw bugs.',
};

interface TopBuggyComponentsProps {
  resolvedPeriod: ResolvedPeriod | null;
}

export const TopBuggyComponents: React.FC<TopBuggyComponentsProps> = ({ resolvedPeriod }) => {
  const [metric, setMetric] = useState<Metric>('count');
  const [rows, setRows] = useState<BreakdownRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drill, setDrill] = useState<{ value: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await QualityApi.getTopComponents(
          { period: null, resolvedPeriod },
          { metric }
        );
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) {
          setRows([]);
          setError(err instanceof Error ? err.message : 'Failed to load components');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric, resolvedPeriod?.startDate, resolvedPeriod?.endDate]);

  const maxValue = useMemo(() => {
    if (metric === 'density') {
      return rows.reduce((m, r) => Math.max(m, r.density ?? 0), 0);
    }
    const field: keyof BreakdownRow = metric === 'open' ? 'openCount' : 'count';
    return rows.reduce((m, r) => Math.max(m, Number(r[field] ?? 0)), 0);
  }, [rows, metric]);

  return (
    <div className="chart-card quality-breakdown-card">
      <div className="chart-header">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Layers size={18} />
          Top buggy components
          <span
            className="stat-hint-icon"
            title="Which parts of the product attract the most bugs. Switch to Density to normalize by delivered stories — large components naturally have more raw bugs."
            aria-label="About this chart"
          >
            <Info size={14} />
          </span>
        </h3>
        <div className="quality-breakdown-metric">
          {(Object.keys(METRIC_LABEL) as Metric[]).map((m) => (
            <button
              key={m}
              type="button"
              className={`btn btn-sm ${metric === m ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setMetric(m)}
              title={METRIC_HINT[m]}
            >
              {METRIC_LABEL[m]}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <p className="quality-breakdown__empty">Loading…</p>
      ) : error ? (
        <p className="quality-breakdown__empty">Failed to load: {error}</p>
      ) : rows.length === 0 ? (
        <p className="quality-breakdown__empty">No components found for this Period.</p>
      ) : (
        <ul className="quality-breakdown-list">
          {rows.map((r) => {
            const value =
              metric === 'density' ? (r.density ?? 0) : metric === 'open' ? r.openCount : r.count;
            const pct = maxValue > 0 ? (value / maxValue) * 100 : 0;
            return (
              <li
                key={r.key}
                className="quality-breakdown-row"
                onClick={() => setDrill({ value: r.key })}
              >
                <span className="quality-breakdown-row__label" title={r.key}>
                  {r.key}
                </span>
                <div className="quality-breakdown-row__bar">
                  <div className="quality-breakdown-row__fill" style={{ width: `${pct}%` }} />
                </div>
                <span className="quality-breakdown-row__value">
                  {metric === 'density' ? (value as number).toFixed(2) : value}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {drill && (
        <DrillDownModal
          by="component"
          value={drill.value}
          resolvedPeriod={resolvedPeriod}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
};
